import { QuestInteractionHandler } from "@/app/games/(details)/QuestInteractionHandler";
import { GemsBadge } from "@/app/games/(details)/quest-badges";
import { QuestTaskItem } from "@/app/games/(details)/quest-group";
import { useGameDetails } from "@/app/games/(details)/useGameDetails";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { useHomepageAnalytics } from "@/context/AnalyticsProvider/homepage";
import { cn } from "@/lib/utils";
import { formatGenreLabel } from "@/utils/string";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useMemo } from "react";
import type { KeyedMutator } from "swr";
import type {
  CampaignQuest,
  CampaignQuestState,
  QuestCompletion,
  QuestCompletions,
} from "../QuestsV2/types";
import { List } from "../ui/list";

interface QuestCardProps {
  gameName: string;
  completedQuests?: QuestCompletion[];
  invalidateQuestCompletions: KeyedMutator<QuestCompletions>;
  gameIcon?: string;
  gameSlug?: string;
  gameGenres: string[];
  gameLandingPageUrl?: string;
  featured: boolean;
  gameId: string;
  quests: CampaignQuestState[];
  displayOrder: number;
}

export const QuestCard = ({
  gameName,
  completedQuests,
  invalidateQuestCompletions,
  gameIcon,
  gameSlug,
  gameId,
  gameGenres,
  gameLandingPageUrl,
  quests,
  displayOrder,
}: QuestCardProps) => {
  const { track } = useHomepageAnalytics();
  const {
    onQuestClick,
    isWishlisted,
    completeQuest,
    allQuests,
    inProgressQuest,
    setInProgressQuest,
  } = useGameDetails(
    {
      quests,
      details: {
        game_id: gameId,
        name: gameName,
        landing_page_url: gameLandingPageUrl,
      },
    },
    completedQuests,
    invalidateQuestCompletions,
  );

  // Calculate progress based on total rewards earned vs total possible gems
  const totalGemsEarned =
    allQuests?.reduce((sum, quest) => {
      // For completed quests, multiply gems by completion count (for recurring quests)
      if (
        quest.completed &&
        quest.completionCount &&
        quest.completionCount > 0
      ) {
        return sum + quest.gems * quest.completionCount;
      }
      return sum;
    }, 0) ?? 0;

  const totalPossibleGems =
    allQuests?.reduce((sum, quest) => sum + quest.gems, 0) ?? 0;

  // Ensure progress doesn't exceed 100% for recurring quests
  const progressPercentage =
    totalPossibleGems > 0
      ? Math.min((totalGemsEarned / totalPossibleGems) * 100, 100)
      : 0;

  const formattedGameGenres = useMemo(() => {
    return gameGenres?.map(formatGenreLabel).join(", ") || "";
  }, [gameGenres]);

  if (quests.length === 0) {
    return null;
  }

  const handleCompleteQuest = async (quest: CampaignQuest) => {
    const completedQuest = await completeQuest(quest);
    if (completedQuest) {
      track({
        action: Action.SUCCEEDED,
        extras: {
          id: "quest-finder",
          gameId: gameId,
          gameName: gameName,
          questId: quest.id.toString(),
          questType: quest.type,
          displayOrder: displayOrder,
        },
      });
    } else {
      track({
        action: Action.FAILED,
        extras: {
          id: "quest-finder",
          gameId,
          gameName,
          questId: quest.id.toString(),
          questType: quest.type,
          displayOrder: displayOrder,
        },
      });
    }
  };

  const handleQuestClick = (quest: CampaignQuestState) => {
    track({
      action: Action.PRESSED,
      extras: {
        id: "quest-finder",
        control: "quest",
        controlType: ControlType.LIST_ITEM,
        gameId: gameId,
        gameName: gameName,
        questId: quest.id.toString(),
        questType: quest.type,
        displayOrder: displayOrder,
      },
    });
    onQuestClick(quest);
  };

  const handleGameClick = () => {
    track({
      action: Action.PRESSED,
      extras: {
        id: "quest-finder",
        control: "ViewGamePage",
        controlType: ControlType.BUTTON,
        gameId: gameId,
        gameName: gameName,
        displayOrder: displayOrder,
      },
    });
  };
  return (
    <>
      <Card
        className={cn(
          "bg-neutral-900/80 border-white/10 transition-all",
          gameSlug &&
            "cursor-pointer hover:-translate-y-0.5 hover:shadow-xl hover:bg-neutral-900/90 hover:border-white/20",
        )}
      >
        <CardContent className="p-4">
          {/* Game Header */}
          <div className="flex gap-3 mb-2">
            <div
              className={cn(
                "size-12 rounded-lg flex items-center justify-center shrink-0 overflow-hidden",
                gameIcon
                  ? "border-2 border-white/20 bg-neutral-800/70"
                  : "bg-transparent",
              )}
            >
              <Image
                src={gameIcon || "/images/rewards/playgame.svg"}
                alt={gameIcon ? gameName : "Play Game"}
                width={gameIcon ? 44 : 48}
                height={gameIcon ? 44 : 48}
                className="object-contain"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-white">{gameName}</h3>
              {gameGenres && (
                <p className="text-xs text-white/60">{formattedGameGenres}</p>
              )}
            </div>
            {gameSlug && (
              <Link
                href={`/games/${gameSlug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white"
                onClick={() => handleGameClick()}
              >
                <ArrowRight className="size-5" />
              </Link>
            )}
          </div>

          {/* Progress Bar with Completion Badge */}
          <div className="flex items-center gap-2 mb-4">
            <Progress
              value={progressPercentage}
              className="h-1.5 bg-neutral-800/60 flex-1"
            />
            <GemsBadge
              text={`${totalGemsEarned} / ${Math.max(totalPossibleGems, totalGemsEarned)}`}
              completed={totalGemsEarned > 0}
            />
          </div>

          {/* Quest List */}
          <div className="border-t border-border pt-5 max-h-[600px] overflow-y-scroll">
            <List variant="flush" size="md">
              {allQuests?.map((quest) => (
                <QuestTaskItem
                  key={quest.id}
                  quest={quest}
                  disabled={!isWishlisted && quest.type !== "wishlist"}
                  onClick={() => handleQuestClick(quest)}
                />
              ))}
            </List>
          </div>
        </CardContent>
        <QuestInteractionHandler
          game={{
            details: {
              game_id: gameId,
              name: gameName,
              slug: gameSlug,
            },
            quests,
          }}
          completeQuest={handleCompleteQuest}
          inProgressQuest={inProgressQuest}
          setInProgressQuest={setInProgressQuest}
        />
      </Card>
    </>
  );
};
